import { useState } from "react";
import { ChevronRight, ChevronDown, GitBranch } from "lucide-react";

/* ---------- shared primitives ---------- */

const Arrow = ({ label }) => (
  <div aria-hidden="true" className="flex shrink-0 items-center justify-center text-[var(--text-muted)]">
    <ChevronDown size={20} className="md:hidden" />
    <div className="hidden flex-col items-center md:flex">
      <ChevronRight size={20} />
      {label && <span className="font-mono text-[0.58rem] uppercase tracking-wider">{label}</span>}
    </div>
  </div>
);

const RevealPanel = ({ children, testid }) => (
  <div
    role="status"
    aria-live="polite"
    data-testid={testid}
    className="mt-5 rounded-xl border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3 text-sm leading-relaxed text-[var(--text-2)]"
  >
    {children}
  </div>
);

const DiagramFrame = ({ children, label, testid }) => (
  <figure className="ic-surface p-5 sm:p-6" data-testid={testid} role="group" aria-label={label}>
    {children}
  </figure>
);

/* ---------- Q1 priorities (3 stage) ---------- */

export const PriorityVisual = ({ stages }) => {
  const [sel, setSel] = useState(null);
  const active = stages.find((s) => s.id === sel);
  return (
    <DiagramFrame label="Three-stage approach to turning commitments into customer value" testid="q1-priority-diagram">
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-stretch">
        {stages.map((s, i) => (
          <div key={s.id} className="flex flex-1 flex-col md:flex-row md:items-stretch">
            <button
              type="button"
              className="ic-node flex flex-1 flex-col gap-1"
              aria-pressed={sel === s.id}
              onClick={() => setSel(sel === s.id ? null : s.id)}
              data-testid={`q1-priority-stage-${i + 1}`}
            >
              <span className="font-mono text-[0.62rem] text-[var(--accent)]">STAGE {i + 1}</span>
              <span className="font-head text-base font-semibold leading-snug">{s.label}</span>
              <span className="text-sm leading-relaxed text-[var(--text-2)]">{s.body}</span>
            </button>
            {i < stages.length - 1 && <Arrow />}
          </div>
        ))}
      </div>
      {active && <RevealPanel testid="q1-priority-reveal">Selected: <strong className="text-[var(--text)]">{active.label}</strong> — {active.body}</RevealPanel>}
    </DiagramFrame>
  );
};

/* ---------- RSC partner-sequencing ---------- */

const RSC_STAGES = [
  { id: "problem", label: "Customer problem", body: "Enterprise recruiters manually copied candidate data between LinkedIn Recruiter and their ATS — duplicated outreach, no shared status, and repetitive candidate contact." },
  { id: "reciprocal", label: "Reciprocal value", body: "I shaped a reciprocal model: ATS recruiting data would surface inside Recruiter, and a valuable Recruiter experience would surface inside the ATS." },
  { id: "midmarket", label: "Mid-market launches", emphasis: true, body: "The pivotal decision: when the largest ATS providers stayed reluctant, I shifted the sequence to motivated mid-market partners wanting to differentiate and move upmarket." },
  { id: "proof", label: "Customer proof and broader engagement", body: "Early traction and customer advocacy built the evidence and momentum to re-engage larger providers." },
];

const RscVisual = ({ compact }) => {
  const [sel, setSel] = useState(null);
  const active = RSC_STAGES.find((s) => s.id === sel);

  if (compact) {
    return (
      <DiagramFrame label="RSC partner-sequencing story" testid="q2-rsc-partner-diagram">
        <div className="flex flex-col items-stretch gap-2 md:flex-row">
          {RSC_STAGES.map((s, i) => (
            <div key={s.id} className="flex flex-1 flex-col md:flex-row md:items-center">
              <div className={`ic-node flex-1 cursor-default ${s.emphasis ? "ic-node-emphasis" : ""}`}>
                <span className="font-mono text-[0.58rem] text-[var(--text-muted)]">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-head text-sm font-semibold leading-snug">{s.label}</p>
                {s.emphasis && <span className="ic-pill mt-2">Pivotal decision</span>}
              </div>
              {i < RSC_STAGES.length - 1 && <Arrow />}
            </div>
          ))}
        </div>
      </DiagramFrame>
    );
  }

  return (
    <DiagramFrame label="RSC partner-sequencing story — select a stage for detail" testid="case-visual-rsc">
      <div className="flex flex-col items-stretch gap-2 md:flex-row">
        {RSC_STAGES.map((s, i) => (
          <div key={s.id} className="flex flex-1 flex-col md:flex-row md:items-stretch">
            <button
              type="button"
              className={`ic-node flex flex-1 flex-col gap-1.5 ${s.emphasis ? "ic-node-emphasis" : ""}`}
              aria-pressed={sel === s.id}
              onClick={() => setSel(sel === s.id ? null : s.id)}
              data-testid={`rsc-stage-${s.id}`}
            >
              <span className="font-mono text-[0.58rem] text-[var(--text-muted)]">STEP {i + 1}</span>
              <span className="font-head text-sm font-semibold leading-snug">{s.label}</span>
              {s.emphasis && <span className="ic-pill">Pivotal decision</span>}
            </button>
            {i < RSC_STAGES.length - 1 && <Arrow />}
          </div>
        ))}
      </div>
      {active && <RevealPanel testid="rsc-reveal">{active.body}</RevealPanel>}
    </DiagramFrame>
  );
};

/* ---------- Procore portfolio filter ---------- */

const PROCORE_LENSES = [
  { id: "value", label: "Customer value", body: "Does this partnership solve a real, recurring customer need?" },
  { id: "product", label: "Product alignment", body: "Does it match the priorities of the product leaders it depends on?" },
  { id: "capacity", label: "Delivery capacity", body: "Can a small team realistically execute and support it?" },
];

const ProcoreVisual = () => {
  const [sel, setSel] = useState(null);
  const active = PROCORE_LENSES.find((s) => s.id === sel);
  return (
    <DiagramFrame label="Qualitative portfolio filter for prioritizing partnerships" testid="case-visual-procore">
      <div className="flex flex-col items-center gap-2">
        <div className="ic-node w-full text-center sm:w-auto sm:px-8">
          <p className="font-head text-sm font-semibold">Many partner opportunities</p>
          <span className="font-mono text-[0.58rem] text-[var(--text-muted)]">Three-person team</span>
        </div>
        <Arrow label="through" />
        <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
          {PROCORE_LENSES.map((l) => (
            <button
              key={l.id}
              type="button"
              className="ic-node text-center"
              aria-pressed={sel === l.id}
              onClick={() => setSel(sel === l.id ? null : l.id)}
              data-testid={`procore-lens-${l.id}`}
            >
              <span className="font-mono text-[0.58rem] text-[var(--accent)]">LENS</span>
              <p className="font-head text-sm font-semibold leading-snug">{l.label}</p>
            </button>
          ))}
        </div>
        <Arrow label="yields" />
        <div className="flex w-full flex-col items-stretch gap-2 md:flex-row">
          <div className="ic-node ic-node-emphasis flex-1 cursor-default text-center">
            <p className="font-head text-sm font-semibold">Prioritized partner program</p>
          </div>
          <Arrow />
          <div className="ic-node flex-1 cursor-default text-center">
            <p className="font-head text-sm font-semibold">Focused execution</p>
          </div>
        </div>
      </div>
      {active && <RevealPanel testid="procore-reveal"><strong className="text-[var(--text)]">{active.label}:</strong> {active.body}</RevealPanel>}
    </DiagramFrame>
  );
};

/* ---------- LMS enterprise value map ---------- */

const LMS_JOBS = [
  { id: "assign", label: "Assign learning", body: "Admins push the right courses into the tools employees already use." },
  { id: "track", label: "Track completion", body: "Completion and compliance records flow back automatically." },
  { id: "report", label: "Report on programs", body: "Learning shows up inside the customer's existing reporting." },
];

const LmsVisual = () => {
  const [sel, setSel] = useState(null);
  const active = LMS_JOBS.find((s) => s.id === sel);
  return (
    <DiagramFrame label="Enterprise value map connecting LinkedIn Learning to the customer's LMS" testid="case-visual-lms">
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center">
        <div className="ic-node ic-node-emphasis flex-1 cursor-default text-center">
          <p className="font-head text-sm font-semibold">LinkedIn Learning</p>
        </div>
        <Arrow label="integrates with" />
        <div className="ic-node flex-1 cursor-default text-center">
          <p className="font-head text-sm font-semibold">Customer's existing LMS</p>
        </div>
      </div>
      <p className="mt-4 mb-2 font-mono text-[0.6rem] uppercase tracking-wider text-[var(--text-muted)]">Customer jobs</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {LMS_JOBS.map((j) => (
          <button
            key={j.id}
            type="button"
            className="ic-node text-center"
            aria-pressed={sel === j.id}
            onClick={() => setSel(sel === j.id ? null : j.id)}
            data-testid={`lms-job-${j.id}`}
          >
            <p className="font-head text-sm font-semibold leading-snug">{j.label}</p>
          </button>
        ))}
      </div>
      <figcaption className="mt-4 text-xs italic text-[var(--text-muted)]">
        Integration quality influenced buying, deployment, and ongoing administration.
      </figcaption>
      {active && <RevealPanel testid="lms-reveal"><strong className="text-[var(--text)]">{active.label}:</strong> {active.body}</RevealPanel>}
    </DiagramFrame>
  );
};

/* ---------- LXP discovery network with boundaries ---------- */

const LXP_GUARDRAILS = [
  { id: "agreements", label: "Separate enterprise agreements", body: "Providers kept their own customer contracts — discovery did not replace them." },
  { id: "enabled", label: "Customer-enabled integrations", body: "Customers chose to switch discovery on, keeping control of participation." },
  { id: "boundaries", label: "Defined content boundaries", body: "Clear limits on how content was surfaced, not transferred or re-hosted." },
];

const LxpVisual = () => {
  const [sel, setSel] = useState(null);
  const active = LXP_GUARDRAILS.find((s) => s.id === sel);
  const providers = ["Coursera", "Udemy", "Other providers"];
  return (
    <DiagramFrame label="Multi-provider discovery network with explicit participation boundaries" testid="case-visual-lxp">
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center">
        <div className="flex flex-1 flex-col gap-2">
          {providers.map((p) => (
            <div key={p} className="ic-node cursor-default text-center md:text-left">
              <p className="font-head text-sm font-semibold">{p}</p>
            </div>
          ))}
        </div>
        <Arrow label="discovery" />
        <div className="ic-node ic-node-emphasis flex-1 cursor-default text-center">
          <p className="font-head text-sm font-semibold">Unified learning discovery</p>
        </div>
        <Arrow />
        <div className="ic-node flex-1 cursor-default text-center">
          <p className="font-head text-sm font-semibold">Enterprise learners</p>
        </div>
      </div>
      <p className="mt-4 mb-2 font-mono text-[0.6rem] uppercase tracking-wider text-[var(--text-muted)]">Participation boundaries</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {LXP_GUARDRAILS.map((g) => (
          <button
            key={g.id}
            type="button"
            className="ic-node text-center"
            aria-pressed={sel === g.id}
            onClick={() => setSel(sel === g.id ? null : g.id)}
            data-testid={`lxp-guardrail-${g.id}`}
          >
            <p className="font-head text-xs font-semibold leading-snug">{g.label}</p>
          </button>
        ))}
      </div>
      {active && <RevealPanel testid="lxp-reveal"><strong className="text-[var(--text)]">{active.label}:</strong> {active.body}</RevealPanel>}
    </DiagramFrame>
  );
};

/* ---------- Application before / after framework ---------- */

const ApplicationVisual = () => {
  const before = ["Fragmented experiences", "Inconsistent implementation", "Widely distributed API"];
  const after = ["Clearer data flows", "Defined integration requirements", "Coordinated candidate experience"];
  return (
    <DiagramFrame label="Before and after framework for application integrations" testid="case-visual-application">
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center">
        <div className="flex-1 rounded-xl border border-[var(--border)] bg-[var(--surface-2)] p-4">
          <p className="mb-3 font-mono text-[0.6rem] uppercase tracking-wider text-[var(--text-muted)]">Before</p>
          <ul className="space-y-2">
            {before.map((b) => (
              <li key={b} className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm text-[var(--text-2)]">{b}</li>
            ))}
          </ul>
        </div>
        <div className="flex shrink-0 flex-col items-center justify-center px-1 text-[var(--accent)]">
          <ChevronDown size={22} className="md:hidden" />
          <ChevronRight size={22} className="hidden md:block" />
          <span className="font-mono text-[0.56rem] uppercase tracking-wider text-[var(--text-muted)]">structured framework</span>
        </div>
        <div className="flex-1 rounded-xl p-4" style={{ border: "1px solid var(--border-accent)", background: "var(--badge-bg)" }}>
          <p className="mb-3 font-mono text-[0.6rem] uppercase tracking-wider text-[var(--accent)]">After</p>
          <ul className="space-y-2">
            {after.map((a) => (
              <li key={a} className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm">{a}</li>
            ))}
          </ul>
        </div>
      </div>
    </DiagramFrame>
  );
};

/* ---------- Jobs distribution decision timeline with branch ---------- */

const JOBS_PATHS = {
  build: "Built the initiative and a distributed team to pursue search visibility and distribution partners.",
  develop: "Developed distribution opportunities across government, libraries, higher education, and search partners.",
  assess: "Assessed the evidence and the underlying product and SEO limitations.",
  continue: "Continuing external investment would not overcome the product and technical constraints.",
  redirect: "I recommended redirecting investment toward product and engineering — the foundation the demand needed.",
};

const JobsVisual = () => {
  const [sel, setSel] = useState(null);
  const steps = [
    { id: "build", label: "Build the initiative" },
    { id: "develop", label: "Develop distribution" },
    { id: "assess", label: "Assess the evidence" },
  ];
  return (
    <DiagramFrame label="Decision timeline ending in a recommendation to redirect investment" testid="case-visual-jobs">
      <div className="flex flex-col items-stretch gap-2 md:flex-row md:items-center">
        {steps.map((s, i) => (
          <div key={s.id} className="flex flex-1 flex-col md:flex-row md:items-stretch">
            <button
              type="button"
              className="ic-node flex-1"
              aria-pressed={sel === s.id}
              onClick={() => setSel(sel === s.id ? null : s.id)}
              data-testid={`jobs-step-${s.id}`}
            >
              <span className="font-mono text-[0.58rem] text-[var(--text-muted)]">STEP {i + 1}</span>
              <p className="font-head text-sm font-semibold leading-snug">{s.label}</p>
            </button>
            <Arrow />
          </div>
        ))}
        <div className="flex items-center justify-center text-[var(--accent)] md:px-1">
          <GitBranch size={20} aria-hidden="true" />
        </div>
      </div>
      <div className="mt-2 flex flex-col gap-2 md:ml-[8%] md:flex-row">
        <button
          type="button"
          className="ic-node flex-1 opacity-80"
          aria-pressed={sel === "continue"}
          onClick={() => setSel(sel === "continue" ? null : "continue")}
          data-testid="jobs-path-continue"
        >
          <span className="font-mono text-[0.58rem] text-[var(--text-muted)]">PATH A</span>
          <p className="font-head text-sm font-medium leading-snug text-[var(--text-2)]">Continue external investment</p>
        </button>
        <button
          type="button"
          className="ic-node ic-node-emphasis flex-1"
          aria-pressed={sel === "redirect"}
          onClick={() => setSel(sel === "redirect" ? null : "redirect")}
          data-testid="jobs-path-redirect"
        >
          <span className="font-mono text-[0.58rem] text-[var(--accent)]">RECOMMENDATION</span>
          <p className="font-head text-sm font-semibold leading-snug">Redirect toward product and engineering</p>
        </button>
      </div>
      {sel && <RevealPanel testid="jobs-reveal">{JOBS_PATHS[sel]}</RevealPanel>}
    </DiagramFrame>
  );
};

/* ---------- dispatcher ---------- */

export const CaseVisual = ({ visual, compact }) => {
  switch (visual) {
    case "rsc":
      return <RscVisual compact={compact} />;
    case "procore":
      return <ProcoreVisual />;
    case "lms":
      return <LmsVisual />;
    case "lxp":
      return <LxpVisual />;
    case "application":
      return <ApplicationVisual />;
    case "jobs":
      return <JobsVisual />;
    default:
      return null;
  }
};

// Compact non-interactive preview for the /work cards.
export const CasePreview = ({ visual }) => {
  const labels = {
    rsc: ["Customer problem", "Reciprocal value", "Mid-market", "Proof"],
    procore: ["Opportunities", "3 decision lenses", "Prioritized program"],
    lms: ["LinkedIn Learning", "Customer LMS", "3 customer jobs"],
    lxp: ["Multiple providers", "Unified discovery", "Learners"],
    application: ["Before: fragmented", "After: structured"],
    jobs: ["Build", "Assess", "Redirect ✓"],
  };
  const items = labels[visual] || [];
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-hidden="true">
      {items.map((t, i) => (
        <span key={t} className="inline-flex items-center gap-1.5">
          <span className="rounded-md border border-[var(--border)] bg-[var(--surface-2)] px-2 py-1 text-[0.68rem] text-[var(--text-2)]">{t}</span>
          {i < items.length - 1 && <ChevronRight size={12} className="text-[var(--text-muted)]" />}
        </span>
      ))}
    </div>
  );
};
